import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { ErrorNotice } from './Layout';

type Kind =
  'meeting_topic' | 'meeting_label' | 'document_category' | 'document_agency' | 'document_label';
type Member = { kind: Kind; id: string };
type Group = { id: string; name: string; color: string; members: Member[] };
type Item = { id: string; name: string; color: string; systemKey?: string | null };
const sections: { kind: Kind; title: string; url: string; queryKey: string[] }[] = [
  {
    kind: 'meeting_topic',
    title: 'หมวดประชุม',
    url: '/api/meeting-topics?status=active&limit=100',
    queryKey: ['meeting-topics'],
  },
  {
    kind: 'meeting_label',
    title: 'Label ประชุม',
    url: '/api/meeting-labels',
    queryKey: ['meeting-labels'],
  },
  {
    kind: 'document_category',
    title: 'ประเภทเอกสาร',
    url: '/api/document-taxonomy/categories',
    queryKey: ['document-taxonomy', 'categories'],
  },
  {
    kind: 'document_agency',
    title: 'หน่วยงานเอกสาร',
    url: '/api/document-taxonomy/agencies',
    queryKey: ['document-taxonomy', 'agencies'],
  },
  {
    kind: 'document_label',
    title: 'Label เอกสาร',
    url: '/api/document-taxonomy/labels',
    queryKey: ['document-taxonomy', 'labels'],
  },
];
const keyOf = (member: Member) => `${member.kind}:${member.id}`;

export function ColorGroupSettings() {
  const client = useQueryClient();
  const groups = useQuery({
    queryKey: ['color-groups'],
    queryFn: () => api<{ data: Group[] }>('/api/color-groups').then((r) => r.data),
  });
  const topics = useQuery({
    queryKey: ['meeting-topics', 'settings'],
    queryFn: () =>
      api<{ data: Array<{ id: string; title: string; color: string; systemKey?: string | null }> }>(
        sections[0].url,
      ).then((r) => r.data.map((item) => ({ ...item, name: item.title }))),
  });
  const labels = useQuery({
    queryKey: ['meeting-labels'],
    queryFn: () => api<{ data: Item[] }>(sections[1].url).then((r) => r.data),
  });
  const categories = useQuery({
    queryKey: ['document-taxonomy', 'categories'],
    queryFn: () => api<{ data: Item[] }>(sections[2].url).then((r) => r.data),
  });
  const agencies = useQuery({
    queryKey: ['document-taxonomy', 'agencies'],
    queryFn: () => api<{ data: Item[] }>(sections[3].url).then((r) => r.data),
  });
  const documentLabels = useQuery({
    queryKey: ['document-taxonomy', 'labels'],
    queryFn: () => api<{ data: Item[] }>(sections[4].url).then((r) => r.data),
  });
  const items = [topics.data, labels.data, categories.data, agencies.data, documentLabels.data];
  const [editing, setEditing] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [color, setColor] = useState('#0f766e');
  const [selected, setSelected] = useState<string[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const clear = () => {
    setEditing(null);
    setName('');
    setColor('#0f766e');
    setSelected([]);
    setFormOpen(false);
  };
  const save = useMutation({
    mutationFn: () =>
      api(editing ? `/api/color-groups/${editing}` : '/api/color-groups', {
        method: editing ? 'PATCH' : 'POST',
        body: JSON.stringify({
          name: name.trim(),
          color,
          members: selected.map((key) => {
            const colon = key.indexOf(':');
            return { kind: key.slice(0, colon), id: key.slice(colon + 1) };
          }),
        }),
      }),
    onSuccess: async () => {
      clear();
      await Promise.all([
        client.invalidateQueries({ queryKey: ['color-groups'] }),
        ...sections.map((section) => client.invalidateQueries({ queryKey: section.queryKey })),
      ]);
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/color-groups/${id}`, { method: 'DELETE' }),
    onSuccess: () => client.invalidateQueries({ queryKey: ['color-groups'] }),
  });
  const begin = (group: Group) => {
    setEditing(group.id);
    setName(group.name);
    setColor(group.color);
    setSelected(group.members.map(keyOf));
    setFormOpen(true);
    save.reset();
  };
  const owner = (key: string) =>
    groups.data?.find(
      (group) => group.id !== editing && group.members.some((member) => keyOf(member) === key),
    );
  return (
    <section className="card space-y-4">
      <div>
        <h2 className="font-semibold">กลุ่มสี</h2>
        <p className="mt-1 text-sm text-slate-500">
          เลือกหลายรายการจาก 5 ประเภทให้ใช้สีเดียวกัน เมื่อแก้สีกลุ่ม สมาชิกทั้งหมดจะเปลี่ยนตาม หากแก้สีรายการเดี่ยว รายการนั้นจะออกจากกลุ่ม
        </p>
      </div>
      {groups.data?.map((group) => (
        <div key={group.id} className="flex items-center gap-3 rounded-xl border p-3">
          <span
            className="h-5 w-5 shrink-0 rounded-full border"
            style={{ backgroundColor: group.color }}
          />
          <div className="min-w-0 flex-1">
            <strong>{group.name}</strong>
            <p className="text-xs text-slate-500">{group.members.length} รายการ</p>
          </div>
          <button
            type="button"
            className="text-sm font-semibold text-teal-700"
            onClick={() => begin(group)}
          >
            แก้ไข
          </button>
          <button
            type="button"
            className="text-sm font-semibold text-red-700"
            onClick={() => {
              if (confirm(`ลบกลุ่มสี “${group.name}”? สีของรายการจะคงเดิม`))
                remove.mutate(group.id);
            }}
          >
            ลบ
          </button>
        </div>
      ))}
      {!formOpen ? (
        <button type="button" className="btn-secondary w-full" onClick={() => setFormOpen(true)}>
          + สร้างกลุ่มสี
        </button>
      ) : (
        <div className="space-y-3 rounded-xl border p-3">
          <div className="grid grid-cols-[1fr_4rem] gap-2">
            <label>
              <span className="label">ชื่อกลุ่มสี</span>
              <input
                className="field"
                value={name}
                onChange={(event) => setName(event.target.value)}
                maxLength={80}
                placeholder="เช่น หน่วยงานกำกับ"
              />
            </label>
            <label>
              <span className="label">สี</span>
              <input
                type="color"
                className="h-11 w-full rounded border p-1"
                value={color}
                onChange={(event) => setColor(event.target.value)}
              />
            </label>
          </div>
          <p className="text-sm font-medium">เลือกรายการในกลุ่ม</p>
          {sections.map((section, index) => (
            <div key={section.kind}>
              <h3 className="mb-1 text-sm font-semibold">{section.title}</h3>
              <div className="grid max-h-40 gap-1 overflow-y-auto rounded-lg border p-2">
                {items[index]
                  ?.filter((item) => !item.systemKey)
                  .map((item) => {
                    const key = keyOf({ kind: section.kind, id: item.id });
                    const other = owner(key);
                    return (
                      <label key={key} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={selected.includes(key)}
                          onChange={(event) =>
                            setSelected((current) =>
                              event.target.checked
                                ? [...current, key]
                                : current.filter((value) => value !== key),
                            )
                          }
                        />
                        <span
                          className="h-3 w-3 rounded-full"
                          style={{ backgroundColor: item.color }}
                        />
                        <span>{item.name}</span>
                        {other && (
                          <span className="text-xs text-amber-700">ย้ายจาก {other.name}</span>
                        )}
                      </label>
                    );
                  }) ?? <span className="text-xs text-slate-500">กำลังโหลด…</span>}
              </div>
            </div>
          ))}
          <p className="text-xs text-slate-500">
            หนึ่งรายการอยู่ได้เพียงหนึ่งกลุ่ม หากย้ายเข้ากลุ่มใหม่ สีจะเปลี่ยนตามกลุ่มใหม่
            เมื่อนำออกจะคงสีล่าสุด
          </p>
          <div className="flex gap-2">
            <button type="button" className="btn-secondary flex-1" onClick={clear}>
              ยกเลิก
            </button>
            <button
              type="button"
              className="btn-primary flex-1"
              disabled={!name.trim() || save.isPending}
              onClick={() => save.mutate()}
            >
              {save.isPending ? 'กำลังบันทึก…' : 'บันทึกกลุ่มสี'}
            </button>
          </div>
        </div>
      )}
      {(groups.error || save.error || remove.error) && (
        <ErrorNotice
          message={(groups.error ?? save.error ?? remove.error)?.message ?? 'ดำเนินการไม่สำเร็จ'}
        />
      )}
    </section>
  );
}
